/* =========================================================================
   DECOY — Custom store-rating badge + Klaviyo reviews drawer
   -------------------------------------------------------------------------
   The product page is a custom SPA, so we cannot use Klaviyo's theme-app-block
   installer. Instead we:
     1. Render a custom, DECOY-styled store-rating badge inside
        #PRODUCT_INFO_PADDED (under the price, above the description). The
        renderer (product-page.js) drops an empty #PRODUCT_REVIEW_BADGE there
        on every product/variant render; we (re)populate it here.
        Rating + count are read LIVE from the Klaviyo all-products feed
        (data-id="all") in the drawer, falling back to the config values.
     2. Open that Klaviyo feed in a drawer that pops in over the product page.
   The same feed also lives on the standalone /pages/reviews page for SEO.

   >>> EDIT THIS BLOCK <<<
   ========================================================================= */
window.DECOY_REVIEWS_CONFIG = {
  // Klaviyo public API key (6-character company ID). Used by the /pages/reviews
  // page loader. On the SPA page Klaviyo.js is already injected by the
  // Klaviyo email/SMS onsite embed, so the widget there hydrates automatically.
  companyId: 'WdHeTD',

  // Fallback rating shown instantly and used only if the live widget can't be
  // parsed. Keep roughly in sync with reality as a safety net.
  fallbackRating: 5.0,  // average out of 5
  fallbackCount: 18     // instant placeholder only — the live count from the
                        // Klaviyo widget overrides this. Left intentionally at
                        // 18 so a badge reading of 19 proves the live sync works.
};
/* ===================== END USER-EDITABLE CONFIG ========================== */

(function () {
  'use strict';

  var cfg = window.DECOY_REVIEWS_CONFIG;

  // Current store-wide aggregate (starts at fallback, upgraded by live read).
  var state = {
    rating: Math.max(0, Math.min(5, Number(cfg.fallbackRating) || 0)),
    count: Number(cfg.fallbackCount) || 0
  };

  /* ---- Create/insert + populate the badge inside #PRODUCT_INFO_PADDED ----
     BUILD_PRODUCT (main.js) sets the panel to: price + Product.description,
     where the description starts with the title <h1>. We insert the badge
     right after that <h1> (under the title), falling back to after the price. */
  function ensureBadge() {
    var info = document.getElementById('PRODUCT_INFO_PADDED');
    if (!info) return;

    var sig = state.rating + '/' + state.count;
    var badge = info.querySelector('#PRODUCT_REVIEW_BADGE');
    if (badge && badge.dataset.built === sig) return; // already current & placed

    if (!badge) {
      badge = document.createElement('div');
      badge.id = 'PRODUCT_REVIEW_BADGE';
      var h1 = info.querySelector('h1:not(.lv2-pdp-h1)');
      var price = info.querySelector('.productPriceInline');
      if (h1) {
        h1.insertAdjacentElement('afterend', badge);
      } else if (price) {
        price.insertAdjacentElement('afterend', badge);
      } else {
        info.appendChild(badge);
      }
    }

    var pct = (state.rating / 5) * 100;
    var reviewWord = state.count + ' review' + (state.count === 1 ? '' : 's');

    badge.innerHTML =
      '<div class="drb-top">' +
        '<span class="drb-stars" role="img" aria-label="' + state.rating.toFixed(1) + ' out of 5 stars">' +
          '<span class="drb-stars-empty">★★★★★</span>' +
          '<span class="drb-stars-fill" style="width:' + pct + '%">★★★★★</span>' +
        '</span>' +
        '<span class="drb-value">' + state.rating.toFixed(1) + '</span>' +
        /* A real link to /pages/reviews so crawlers can reach the reviews page;
           a plain click still opens the drawer, a modified click opens a tab. */
        '<a class="drb-count" href="/pages/reviews" aria-label="Open all reviews">' +
          reviewWord + ' ›' +
        '</a>' +
      '</div>';

    badge.dataset.built = sig;

    var openers = badge.querySelectorAll('.drb-count');
    for (var i = 0; i < openers.length; i++) {
      openers[i].addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        openDrawer();
      });
    }
  }

  /* ---- Read the live aggregate (rating + count) from the Klaviyo feed ----
     Klaviyo renders the all-products summary inside #klaviyo-reviews-all. Its
     class names are hashed, so we parse defensively:
       - count: first "<number> review(s)" found in the widget text
       - rating: an aria-label like "Rated 5.0 out of 5", else a "x.x out of 5"
         in the text.
     If parsing fails we keep the fallback values. */
  function parseAggregate(root) {
    if (!root) return null;

    var rating = null, count = null;

    // ---- Count ----
    // Do NOT regex the whole widget's textContent: Klaviyo renders the rating
    // ("5 / 5") and the count ("19 reviews") in adjacent nodes with no
    // separator, so the blob reads "5 / 519 reviews" — the rating's trailing
    // "5" glues onto the count. Instead, find the single element whose ENTIRE
    // text is exactly "N review(s)" — that isolates the real count.
    var els = root.querySelectorAll('*');
    for (var j = 0; j < els.length && count === null; j++) {
      var t = (els[j].textContent || '').replace(/\s+/g, ' ').trim();
      var mc = t.match(/^([\d,]+)\s+reviews?$/i);
      if (mc) count = parseInt(mc[1].replace(/,/g, ''), 10);
    }

    // ---- Rating ----
    var labelled = root.querySelectorAll('[aria-label]');
    for (var i = 0; i < labelled.length && rating === null; i++) {
      var lbl = labelled[i].getAttribute('aria-label');
      // Legacy: "Rated 5.0 out of 5" / "4.6 / 5". Current Klaviyo:
      // "Average rating is 5 stars" (also matches per-review "5 star
      // review" — same value, and the aggregate comes first in the DOM).
      var m = lbl.match(/([0-5](?:\.\d+)?)\s*(?:out of|\/)\s*5/i) ||
              lbl.match(/([0-5](?:\.\d+)?)\s*stars?\b/i);
      if (m) rating = parseFloat(m[1]);
    }
    if (rating === null) {
      var text = (root.textContent || '').replace(/\s+/g, ' ').trim();
      var mRate = text.match(/([0-5](?:\.\d+)?)\s*(?:out of|\/)\s*5/i);
      if (mRate) rating = parseFloat(mRate[1]);
    }

    if (rating === null && count === null) return null;
    return { rating: rating, count: count };
  }

  function startLiveAggregate() {
    var source = document.getElementById('klaviyo-reviews-all');
    if (!source) return;

    // Keep the badge in sync with the widget's live aggregate. The Klaviyo
    // widget hydrates lazily (often only once the drawer is first opened) and
    // can re-render afterwards, so we must NOT read once and give up — that
    // left the badge stuck on the hardcoded fallback even after the widget
    // showed the real number. Watch persistently and re-sync on every change.
    function sync() {
      var agg = parseAggregate(source);
      if (!agg) return;
      var changed = false;
      if (agg.rating !== null && agg.rating !== state.rating) {
        state.rating = Math.max(0, Math.min(5, agg.rating));
        changed = true;
      }
      if (agg.count !== null && agg.count !== state.count) {
        state.count = agg.count;
        changed = true;
      }
      if (changed) ensureBadge(); // re-render with live values
    }

    sync(); // in case it's already hydrated
    new MutationObserver(sync).observe(source, { childList: true, subtree: true, characterData: true });

    // Nudge Klaviyo to hydrate the (hidden) widget on load, so the badge can
    // pick up the live count without the user having to open the drawer first.
    nudgeKlaviyoReviews();
  }

  /* Ask Klaviyo's onsite.js to (re)scan + hydrate review widgets on the page.
     onsite.js loads async, so it may not be ready at init — retry briefly. */
  function nudgeKlaviyoReviews() {
    var tries = 0;
    (function go() {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        try { window.klaviyo.push(['trackReviews']); } catch (e) { /* no-op */ }
        return;
      }
      if (tries++ < 20) setTimeout(go, 500); // up to ~10s for onsite.js
    })();
  }

  /* ---- Page scroll lock ----
     DECOY GLASS is a JS-driven SPA — page scrolling is handled by custom
     touch/wheel handlers, not native overflow. CSS `overflow: hidden` on
     #PRODUCT_PAGE has no effect on those handlers. So we capture wheel +
     touchmove at the document root with passive: false, and preventDefault
     unless the target is inside the reviews panel (which scrolls its own
     content). */
  var _scrollBlocker = null;
  function lockPageScroll() {
    if (_scrollBlocker) return;
    _scrollBlocker = function (e) {
      var panel = document.getElementById('DECOY_REVIEWS_PANEL');
      if (panel && panel.contains(e.target)) return; // allow scroll in drawer
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('wheel',     _scrollBlocker, { passive: false, capture: true });
    document.addEventListener('touchmove', _scrollBlocker, { passive: false, capture: true });
  }
  function unlockPageScroll() {
    if (!_scrollBlocker) return;
    document.removeEventListener('wheel',     _scrollBlocker, { capture: true });
    document.removeEventListener('touchmove', _scrollBlocker, { capture: true });
    _scrollBlocker = null;
  }

  /* ---- Blocker fallback ----
     Content blockers / tracking-protection (uBlock, AdGuard, Firefox iOS
     ETP, DuckDuckGo) block static.klaviyo.com, so onsite.js never loads and
     #klaviyo-reviews-all never hydrates — the drawer would otherwise show an
     empty body. This is strictly ADDITIVE and gated on the symptom: it only
     injects a fallback when the widget is still empty after a grace period,
     and it removes itself the instant the real widget hydrates (so a slow
     network just briefly shows it). Users without a blocker never see it. */
  var FALLBACK_ID = 'DECOY_REVIEWS_FALLBACK';

  // Tri-state block detection: null = unknown, true/false once probed.
  // We fetch Klaviyo's own script URL once at init; an ad/tracker blocker
  // or browser tracking-protection rejects it, which tells us up front the
  // widget will never hydrate — so the drawer can show its fallback fast
  // instead of waiting out the full hydration grace. Runs long before a
  // user can navigate to a product and open the drawer. force-cache keeps
  // it free for non-blocked users (onsite.js is already cached).
  var klaviyoBlocked = null;
  function probeKlaviyo() {
    try {
      fetch('https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=' + cfg.companyId,
            { mode: 'no-cors', cache: 'force-cache' })
        .then(function () { klaviyoBlocked = false; })
        .catch(function () { klaviyoBlocked = true; });
    } catch (e) { klaviyoBlocked = true; }
  }

  function widgetHasContent() {
    var w = document.getElementById('klaviyo-reviews-all');
    // Klaviyo injects element children on hydrate; an empty/blocked widget
    // has none (stray text/whitespace nodes don't count).
    return !!(w && w.querySelector('*'));
  }

  function clearReviewsFallback() {
    var el = document.getElementById(FALLBACK_ID);
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  function showReviewsFallback() {
    if (document.getElementById(FALLBACK_ID) || widgetHasContent()) return;
    var body = document.getElementById('DECOY_REVIEWS_BODY');
    if (!body) return;
    var pct = (state.rating / 5) * 100;
    var reviewWord = state.count + ' review' + (state.count === 1 ? '' : 's');
    var box = document.createElement('div');
    box.id = FALLBACK_ID;
    box.innerHTML =
      '<div class="drf-agg" role="img" aria-label="' + state.rating.toFixed(1) + ' out of 5 stars">' +
        '<span class="drb-stars">' +
          '<span class="drb-stars-empty">★★★★★</span>' +
          '<span class="drb-stars-fill" style="width:' + pct + '%">★★★★★</span>' +
        '</span>' +
        '<span class="drf-value">' + state.rating.toFixed(1) + '</span>' +
      '</div>' +
      '<p class="drf-sub">Based on ' + reviewWord + '.</p>' +
      '<p class="drf-msg">We can’t load individual reviews here — an ad/tracker ' +
        'blocker or your browser’s privacy protection is blocking our reviews ' +
        'provider. Allow content for this site, then reopen to read them.</p>';
    body.appendChild(box);
  }

  /* Decide whether the widget is blocked. Called on each drawer open. */
  function monitorReviewsForBlock() {
    var w = document.getElementById('klaviyo-reviews-all');
    if (!w) return;
    if (widgetHasContent()) { clearReviewsFallback(); return; }
    // Remove the fallback the moment real content shows up (slow load).
    var obs = new MutationObserver(function () {
      if (widgetHasContent()) { clearReviewsFallback(); obs.disconnect(); }
    });
    obs.observe(w, { childList: true, subtree: true });
    // Known-blocked → show fast. Otherwise wait out a longer grace so a
    // slow-but-working widget can hydrate before we show anything (avoids
    // a flash for users on slow connections without a blocker).
    var delay = klaviyoBlocked === true ? 500 : 3000;
    setTimeout(function () { if (!widgetHasContent()) showReviewsFallback(); }, delay);
    setTimeout(function () { obs.disconnect(); }, 20000);
  }

  /* ---- Reviews drawer open/close (slides in from right, mirrors cart) ----
     Adds `body.ReviewsOpen` so the page-push CSS rules fire. Closes the
     cart first if it's open — they share the right-side rail and both
     pushing at once would shrink the page off-screen. */
  function openDrawer() {
    var ov = document.getElementById('DECOY_REVIEWS_OVERLAY');
    if (!ov) return;
    if (window.cartOpen && typeof window.toggleCart === 'function') {
      window.toggleCart();
    }
    ov.classList.add('dro-open');
    ov.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ReviewsOpen');
    lockPageScroll();
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['trackReviews']);
      }
    } catch (e) { /* no-op */ }
    monitorReviewsForBlock();
  }

  function closeDrawer() {
    var ov = document.getElementById('DECOY_REVIEWS_OVERLAY');
    if (!ov) return;
    closeDecoyLightbox(); // don't leave an orphaned photo floating over the page
    ov.classList.remove('dro-open');
    ov.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ReviewsOpen');
    unlockPageScroll();
  }

  window.DECOY_openReviews = openDrawer;
  window.DECOY_closeReviews = closeDrawer;

  /* Reviews and cart share the right-side rail; both pushing at once would
     shrink the page off-screen. Wrap the cart's open/toggle entry points
     so reviews close SYNCHRONOUSLY before the cart applies its push (no
     poll, no flash of double-pushed layout). */
  function watchCartOpen() {
    function wrap(name) {
      var orig = window[name];
      if (typeof orig !== 'function' || orig.__decoyReviewsWrapped) return;
      window[name] = function () {
        // Toggle has two phases: opening (cartOpen flips false→true) and
        // closing (true→false). Only intercept on the opening path.
        var willOpen = (name === 'openCart') || (name === 'toggleCart' && !window.cartOpen);
        if (willOpen && document.body.classList.contains('ReviewsOpen')) {
          closeDrawer();
        }
        return orig.apply(this, arguments);
      };
      window[name].__decoyReviewsWrapped = true;
    }
    // The cart's globals are defined in main.js, which loads after us.
    // Try now and again after DOM is fully ready in case ordering changes.
    function tryWrap() { wrap('openCart'); wrap('toggleCart'); }
    tryWrap();
    if (document.readyState !== 'complete') {
      window.addEventListener('load', tryWrap);
    }
    // One more attempt next tick to handle any deferred script ordering.
    setTimeout(tryWrap, 0);
  }

  function bindDrawer() {
    var closeBtn = document.getElementById('DECOY_REVIEWS_CLOSE');
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    // '//' label vocabulary (same as the PDP spec labels). Set from JS so
    // the GLASS markup stays untouched.
    var title = document.querySelector('#DECOY_REVIEWS_HEADER .dro-title');
    if (title) title.textContent = '// REVIEWS';
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });
  }

  /* Reviews are per-product. The SPA navigates back to the landing page by
     removing `body.productOpen` (CLOSE_PRODUCT — via the logo, the close X, or
     a popstate/back to home). Unlike the cart, the reviews drawer should NOT
     persist there: a lingering drawer keeps `body.ReviewsOpen` and its page-push
     applied on the home layout, which breaks it. Watch the body class and close
     the drawer whenever the product closes while it's open. Product→product
     navigation keeps `productOpen` set, so this doesn't fire on those. */
  function watchProductClose() {
    var wasOpen = document.body.classList.contains('productOpen');
    new MutationObserver(function () {
      var isOpen = document.body.classList.contains('productOpen');
      if (wasOpen && !isOpen && document.body.classList.contains('ReviewsOpen')) {
        closeDrawer();
      }
      wasOpen = isOpen;
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  /* The renderer replaces #PRODUCT_INFO_PADDED's children on every product /
     variant change, recreating an empty badge. Watch for that and repopulate. */
  function watchInfoPanel() {
    var info = document.getElementById('PRODUCT_INFO_PADDED');
    if (!info) return;
    new MutationObserver(ensureBadge).observe(info, { childList: true });
  }

  /* ---- Klaviyo widget decorator ----
     Klaviyo's class names are hashed, so CSS alone can't target review rows
     or stars. After the widget hydrates, we:
       1. Find the review-list container (the deepest element with 2+
          same-tag children where each child has substantial text).
       2. Tag those children with `decoy-review-card` so CSS can stripe them.
       3. Replace each star-rating badge (`.kl_reviews__stars_badge`,
          labelled "5 star review" / "Average rating is 5 stars" / legacy
          "x out of 5") with our mask-image star strip, identical to the
          product-page badge.
     Runs continuously via MutationObserver since Klaviyo lazy-renders. */
  function decorateKlaviyoWidget() {
    var root = document.getElementById('klaviyo-reviews-all');
    if (!root) return;

    // 0: Klaviyo stores each product's URL on the shop's myshopify host, which
    // only redirects to the primary domain; point review product cards at this
    // site directly so their links (and the crawl signal) land on decoy.ltd.
    // /pages/reviews (decoy-page-reviews.liquid) applies the same rewrite.
    var hostLinks = root.querySelectorAll('a[href*=".myshopify.com/"]');
    for (var hl = 0; hl < hostLinks.length; hl++) {
      var hu = new URL(hostLinks[hl].href);
      if (/\.myshopify\.com$/.test(hu.hostname)) {
        hostLinks[hl].href = location.origin + hu.pathname + hu.search + hu.hash;
      }
    }

    // 1 + 2: tag review cards. Klaviyo's class names are hashed. Detection
    // strategy: find the container whose DIRECT children each contain a
    // "X out of 5" rating descendant — that's the review list. Each such
    // child is a review card. We pick the container with the MOST rating-
    // bearing children, which beats false-positive containers up in the
    // summary block (aggregate / featured photos).
    var ratingRe = /[\d.]+\s*(?:out of|\/)\s*5/i;
    function isRating(el) {
      var lbl = (el.getAttribute && el.getAttribute('aria-label')) || '';
      return ratingRe.test(lbl);
    }
    // Re-detect every call (cheap, idempotent). Two passes:
    //   pass A — find the parent with the most direct children that contain
    //            an "X out of 5" rating descendant.
    //   pass B — fallback: find the parent with the most direct children
    //            that contain substantial text (each >60 chars). Each
    //            review body is long-text; summary/header children aren't.
    var allEls0 = root.querySelectorAll('*');
    var bestList = null;
    var bestCount = 0;
    for (var ai = 0; ai < allEls0.length; ai++) {
      var cand = allEls0[ai];
      if (cand.children.length < 2) continue;
      var rk = 0;
      for (var bi = 0; bi < cand.children.length; bi++) {
        var kid = cand.children[bi];
        var found = isRating(kid) ? kid : kid.querySelector('[aria-label]');
        if (found && isRating(found)) rk++;
      }
      if (rk > bestCount) { bestCount = rk; bestList = cand; }
    }
    var detectionMode = 'rating';
    if (!bestList || bestCount < 2) {
      detectionMode = 'text';
      for (var ai2 = 0; ai2 < allEls0.length; ai2++) {
        var cand2 = allEls0[ai2];
        if (cand2.children.length < 2) continue;
        var tk = 0;
        for (var bi2 = 0; bi2 < cand2.children.length; bi2++) {
          var kid2 = cand2.children[bi2];
          if ((kid2.textContent || '').trim().length > 60) tk++;
        }
        if (tk > bestCount) { bestCount = tk; bestList = cand2; }
      }
    }
    if (bestList && bestCount >= 2) {
      bestList.classList.add('decoy-review-list');
      // Remove any stale tags from a prior (wrong) detection pass.
      var stale = root.querySelectorAll('.decoy-review-card');
      for (var sk = 0; sk < stale.length; sk++) {
        if (stale[sk].parentElement !== bestList) {
          stale[sk].classList.remove('decoy-review-card', 'decoy-card-even', 'decoy-card-odd');
        }
      }
      var idx = 0;
      for (var ci = 0; ci < bestList.children.length; ci++) {
        var card = bestList.children[ci];
        var keep;
        if (detectionMode === 'rating') {
          keep = card.classList.contains('kl_reviews__review_item');
          if (!keep) {
            if (isRating(card)) { keep = true; }
            else {
              var labelled = card.querySelectorAll('[aria-label]');
              for (var lk = 0; lk < labelled.length; lk++) {
                if (isRating(labelled[lk])) { keep = true; break; }
              }
            }
          }
          // Store reviews (esp. rating-less / imported ones) can lack both the
          // kl_reviews__review_item class AND a detectable star rating, so the
          // checks above drop them — leaving them rendered but unstyled (no
          // card padding, no alternating shade, empty placeholder slots left
          // in). Keep any card that carries a review author or a store-review
          // badge so every real review is styled consistently.
          if (!keep && card.querySelector('[class*="review__author"], [class*="review_author"], [class*="store_review"]')) {
            keep = true;
          }
        } else {
          keep = (card.textContent || '').trim().length > 60;
        }
        if (keep) {
          card.classList.add('decoy-review-card');
          card.classList.remove('decoy-card-even', 'decoy-card-odd');
          card.classList.add(idx % 2 === 1 ? 'decoy-card-even' : 'decoy-card-odd');
          // Collapse any wrapper inside the card that holds no visible
          // text and no media (Klaviyo reserves vertical space for empty
          // placeholder slots — image rows, missing titles, etc.). This
          // catches cases CSS `:empty` / `:not(:has(img))` miss because
          // the wrapper has whitespace text or a placeholder child.
          var emptyKids = card.querySelectorAll('div, section, ul, ol, li, span, p');
          for (var eki = 0; eki < emptyKids.length; eki++) {
            var ek = emptyKids[eki];
            // Never collapse the star strip or any wrapper around it: its
            // mask-painted spans are intentionally text- and media-free.
            if (ek.dataset.decoyStarred ||
                ek.classList.contains('kl_reviews__stars_badge') ||
                ek.closest('.decoy-klav-stars') ||
                ek.querySelector('.kl_reviews__stars_badge, .decoy-klav-stars')) continue;
            if ((ek.textContent || '').trim().length > 0) continue;
            if (ek.querySelector('img, svg, video, iframe, picture, canvas')) continue;
            ek.style.setProperty('display', 'none', 'important');
          }
          idx++;
        }
      }
    }

    // 2b: tag review-media images. Two paths:
    //   a) Every img.kl_reviews__review__image (stable BEM class — the same
    //      one the lightbox interceptor uses) is review media, lone or
    //      stripped; tag it so lone photos render at the same thumbnail
    //      size as strip photos.
    //   b) Any OTHER image only when it lives in a true image strip (its
    //      immediate-wrapper has 2+ image-bearing siblings). Lone untyped
    //      images — like the product thumbnail in a "linked product"
    //      card — are skipped so Klaviyo's native layout is preserved.
    var imgs = root.querySelectorAll('img');
    for (var ii = 0; ii < imgs.length; ii++) {
      var im = imgs[ii];
      if (im.dataset.decoyReviewImg) continue;
      if (im.closest('.decoy-klav-stars')) continue;
      var wrap = im.parentElement;
      if (!wrap || !wrap.parentElement) continue;
      var isReviewPhoto = im.classList.contains('kl_reviews__review__image');
      var strip = wrap.parentElement;
      if (!isReviewPhoto) {
        var stripImgKids = 0;
        for (var ck = 0; ck < strip.children.length; ck++) {
          var sib = strip.children[ck];
          if (sib.tagName === 'IMG' || sib.querySelector('img')) stripImgKids++;
        }
        if (stripImgKids < 2) continue;
      }
      im.classList.add('decoy-review-img');
      im.dataset.decoyReviewImg = '1';
      wrap.classList.add('decoy-img-wrap');
      strip.classList.add('decoy-img-strip');
    }

    // 2b-ii: RIGHT-SIZE THE LINKED-PRODUCT THUMBNAIL.
    //
    // Klaviyo renders a product thumbnail in each review's "linked product"
    // card at 64x64 CSS px, and points it at OUR Shopify CDN image at its
    // ORIGINAL upload size. Measured on a 390px viewport: 11 of them, natural
    // 1200x1200, roughly 85KB each — about 900KB fetched to paint 64px
    // squares, and the single largest block of wasted bytes on the page.
    //
    // The markup is Klaviyo's, so this cannot be fixed at the source; but the
    // URL is Shopify's, and Shopify's CDN resizes on demand from the same
    // `_WxH` suffix the theme already uses everywhere else. Rewriting the src
    // after injection is the only lever available and it is a safe one: if the
    // host is not Shopify, or the URL already carries a size, it is left alone.
    //
    // 192px, not 128: these sit in a scrollable card list on phones that are
    // commonly 3x, and a soft product thumbnail is exactly the kind of detail
    // this brand notices. 192 still costs roughly a fifteenth of the original.
    //
    // Deliberately NOT applied to review PHOTOS — those open in the lightbox,
    // where the user can pinch-zoom and needs the full resolution.
    var prodImgs = root.querySelectorAll('.kl_reviews__review__product_card__container img');
    for (var pi = 0; pi < prodImgs.length; pi++) {
      var pim = prodImgs[pi];
      if (pim.dataset.decoySized) continue;
      var psrc = pim.getAttribute('src') || '';
      if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(psrc)) continue;
      if (/_\d+x\d*\.[a-z]+(\?|$)/i.test(psrc)) { pim.dataset.decoySized = '1'; continue; }
      var sized = psrc.replace(/(\.[a-z]+)(\?.*)?$/i, '_192x192$1$2');
      if (sized !== psrc) {
        pim.setAttribute('src', sized);
        pim.dataset.decoySized = '1';
      }
    }

    // 2c: tag the summary block above the review list. Walk up from the
    // first review card; at every ancestor level, tag the card's preceding
    // siblings. That captures everything in document order before the
    // first card, regardless of how deeply Klaviyo nests its top section.
    // EXCEPT rows holding filter/search controls: those are controls, not
    // part of the inverted aggregate slab — they stay on the base palette.
    var FILTER_SEL = 'input, select, textarea, [role="combobox"], [role="listbox"], [role="searchbox"]';
    var firstCard = root.querySelector('.decoy-review-card');
    if (firstCard) {
      var cur = firstCard;
      while (cur && cur !== root) {
        var p = cur.parentElement;
        if (!p) break;
        var sib2 = p.firstElementChild;
        while (sib2 && sib2 !== cur) {
          if (!sib2.querySelector(FILTER_SEL)) sib2.classList.add('decoy-top-block');
          sib2 = sib2.nextElementSibling;
        }
        cur = p;
      }
      // Purge stale tags from a prior pass (Klaviyo re-renders can move
      // the filter row under a previously-tagged wrapper).
      var tagged = root.querySelectorAll('.decoy-top-block');
      for (var pg = 0; pg < tagged.length; pg++) {
        if (tagged[pg].querySelector(FILTER_SEL)) {
          tagged[pg].classList.remove('decoy-top-block', 'decoy-top-block-outer');
        }
      }
      // Mark outermost top-blocks (no .decoy-top-block ancestor within root)
      // so CSS can give just those the interior padding + bottom border.
      var allTB = root.querySelectorAll('.decoy-top-block');
      for (var ti = 0; ti < allTB.length; ti++) {
        var tb = allTB[ti];
        var anc = tb.parentElement;
        var nested = false;
        while (anc && anc !== root) {
          if (anc.classList && anc.classList.contains('decoy-top-block')) { nested = true; break; }
          anc = anc.parentElement;
        }
        if (!nested) tb.classList.add('decoy-top-block-outer');
      }
    }

    // 2c2: tag the aggregate row inside top-block-outer. Walk up from the
    // aggregate stars host until we hit an element whose text starts with
    // a digit followed by "/ 5" — that row contains "5 / 5  ★★★★★  N reviews".
    var topOuter = root.querySelector('.decoy-top-block-outer');
    if (topOuter) {
      var stale2 = root.querySelectorAll('.decoy-agg-row, .decoy-agg-number');
      for (var sk2 = 0; sk2 < stale2.length; sk2++) {
        stale2[sk2].classList.remove('decoy-agg-row', 'decoy-agg-number');
      }
      var aggStars = topOuter.querySelector('[aria-label]');
      var aggFrac = /^\s*[\d.]+\s*\/\s*5/;
      if (aggStars) {
        var up = aggStars.parentElement;
        while (up && up !== topOuter.parentElement) {
          if (aggFrac.test((up.textContent || '').trim())) {
            up.classList.add('decoy-agg-row');
            // Tag the leaf "5" and the leaf "/ 5".
            var descs = up.querySelectorAll('*');
            var numEl = null, denEl = null;
            for (var di = 0; di < descs.length; di++) {
              var d = descs[di];
              if (d.children.length) continue;
              var t = (d.textContent || '').trim();
              if (!numEl && /^[\d.]+$/.test(t)) { d.classList.add('decoy-agg-number'); numEl = d; }
              else if (!denEl && /^\/\s*5$/.test(t)) { d.classList.add('decoy-agg-denom'); denEl = d; }
              if (numEl && denEl) break;
            }
            // Wrap num + denom in a fraction container so "5 / 5" stays on
            // one line even when agg-row becomes flex-column.
            if (numEl && denEl && !up.querySelector('.decoy-agg-fraction')) {
              // Climb up so we move the highest wrapper of num/denom that
              // isn't shared with stars/count, keeping its inline-block-like
              // rendering intact.
              var numTop = numEl;
              while (numTop.parentElement && numTop.parentElement !== up &&
                     !numTop.parentElement.contains(aggStars)) {
                numTop = numTop.parentElement;
              }
              var denTop = denEl;
              while (denTop.parentElement && denTop.parentElement !== up &&
                     !denTop.parentElement.contains(aggStars)) {
                denTop = denTop.parentElement;
              }
              var frac = document.createElement('div');
              frac.className = 'decoy-agg-fraction';
              numTop.parentNode.insertBefore(frac, numTop);
              frac.appendChild(numTop);
              if (denTop !== numTop && denTop.parentNode) frac.appendChild(denTop);
            }
            break;
          }
          up = up.parentElement;
        }
      }
    }

    // 2d: hide Klaviyo's "Reviews by ..." syndication badge (an SVG that
    // ends up rendered at the bottom of the widget). Identified by the
    // clipPath ID that ships in the SVG's <defs>.
    var badgeNodes = root.querySelectorAll(
      '[id*="syndication-badge"], [clip-path*="syndication-badge"]'
    );
    for (var bb = 0; bb < badgeNodes.length; bb++) {
      var svg = badgeNodes[bb].closest('svg');
      if (svg) svg.classList.add('decoy-syndication-badge');
    }

    // 2e: tag the "N reviews" count line inside the top block so CSS can
    // style it as a '//' spec label. Same entire-text match parseAggregate
    // uses (Klaviyo glues the rating and count together in the text blob,
    // so only a leaf whose ENTIRE text is "N reviews" is safe).
    var topOuterE = root.querySelector('.decoy-top-block-outer');
    if (topOuterE && !topOuterE.querySelector('.decoy-agg-count')) {
      var countCands = topOuterE.querySelectorAll('*');
      for (var ce = 0; ce < countCands.length; ce++) {
        var cel = countCands[ce];
        if (cel.children.length) continue;
        var ct = (cel.textContent || '').replace(/\s+/g, ' ').trim();
        if (/^[\d,]+\s+reviews?$/i.test(ct)) { cel.classList.add('decoy-agg-count'); break; }
      }
    }

    // 3: replace Klaviyo's native star SVGs with the DECOY mask strip — the
    // same construction and geometry as the PDP badge (layout-v2.css
    // `.drb-stars`): two background-painted layers masked to a repeating
    // 5-star strip, the fill layer clipped to `width:<pct>%` of the box.
    // Klaviyo renders stars as `.kl_reviews__stars_badge` (inline SVGs)
    // inside a role="img" host whose aria-label carries the rating. Label
    // formats seen in the wild: "5 star review", "Average rating is 5
    // stars", legacy "4.6 out of 5" / "4.6 / 5".
    var starBadges = root.querySelectorAll('.kl_reviews__stars_badge');
    for (var s = 0; s < starBadges.length; s++) {
      var sb = starBadges[s];
      if (sb.dataset.decoyStarred) continue;
      var rating = null;
      var up2 = sb;
      for (var ul = 0; ul < 4 && up2 && up2 !== root && rating === null; ul++) {
        var lbl2 = (up2.getAttribute && up2.getAttribute('aria-label')) || '';
        var m2 = lbl2.match(/([\d.]+)\s*(?:out of|\/)\s*5/i) ||
                 lbl2.match(/([0-5](?:\.\d+)?)\s*stars?\b/i);
        if (m2) rating = parseFloat(m2[1]);
        up2 = up2.parentElement;
      }
      if (rating === null) {
        // No usable label nearby (e.g. the aggregate stars, whose label
        // lives on a sibling subtree): count Klaviyo's own full/half star
        // SVGs inside the badge.
        rating = sb.querySelectorAll('.kl_reviews__full_star').length +
                 sb.querySelectorAll('.kl_reviews__half_star').length * 0.5;
      }
      if (!(rating >= 0 && rating <= 5)) continue;
      var pct = (rating / 5) * 100;
      sb.innerHTML =
        '<span class="decoy-klav-stars" aria-hidden="true">' +
          '<span class="decoy-klav-stars-empty"></span>' +
          '<span class="decoy-klav-stars-fill" style="width:' + pct + '%"></span>' +
        '</span>';
      sb.dataset.decoyStarred = '1';
    }

    // 4: hairline icon pass. Klaviyo ships filled icons (verified-buyer
    // rosette, edited pencil, search magnifier, dropdown carets, linked-
    // product chevron) that visually compete with the accent stars. Swap
    // each for a thin-stroke equivalent — the page's line-work vocabulary.
    // The svg keeps its box (width/height attrs) so layout never shifts;
    // only the artwork changes. CSS (.decoy-ico) forces fill:none so the
    // blanket svg palette override can't refill them.
    function hairline(host, vb, inner) {
      var svg = host.tagName === 'svg' ? host : host.querySelector('svg');
      if (!svg || svg.dataset.decoyIco) return;
      svg.setAttribute('viewBox', vb);
      svg.setAttribute('fill', 'none');
      svg.innerHTML = inner;
      svg.classList.add('decoy-ico');
      svg.dataset.decoyIco = '1';
    }
    var STROKE = 'stroke="currentColor" fill="none" stroke-linecap="square"';
    var verified = root.querySelectorAll('.kl_reviews__review__verified');
    for (var vi = 0; vi < verified.length; vi++) {
      hairline(verified[vi], '0 0 15 16',
        '<circle cx="7.5" cy="8" r="5.9" ' + STROKE + ' stroke-width="1.1"/>' +
        '<path d="M4.9 8.2l1.9 1.9 3.4-3.9" ' + STROKE + ' stroke-width="1.1"/>');
    }
    var edited = root.querySelectorAll('.kl_reviews__review__edited');
    for (var ei = 0; ei < edited.length; ei++) {
      hairline(edited[ei], '0 0 12 12',
        '<path d="M8.5 1.7l1.8 1.8-6.6 6.6-2.4.7.7-2.4z" ' + STROKE + ' stroke-width="0.9" stroke-linejoin="miter"/>');
    }
    var searchIco = root.querySelectorAll('.kl_reviews__search_icon');
    for (var si = 0; si < searchIco.length; si++) {
      hairline(searchIco[si], '0 0 14 14',
        '<circle cx="6" cy="6" r="4.6" ' + STROKE + ' stroke-width="1.2"/>' +
        '<path d="M9.4 9.4 13.2 13.2" ' + STROKE + ' stroke-width="1.2"/>');
    }
    var carets = root.querySelectorAll('.kl_reviews__filters__pill > svg');
    for (var ci2 = 0; ci2 < carets.length; ci2++) {
      hairline(carets[ci2], '0 0 20 20',
        '<path d="M5.5 8l4.5 4.5L14.5 8" ' + STROKE + ' stroke-width="1.3"/>');
    }
    var carats = root.querySelectorAll('.kl_reviews__review__product_card__carat');
    for (var pi = 0; pi < carats.length; pi++) {
      hairline(carats[pi], '0 0 16 16',
        '<path d="M6.5 3.5 11 8l-4.5 4.5" ' + STROKE + ' stroke-width="1.2"/>');
    }

    // 5: linked-product card — replace the glyph star in "5 ★ · N reviews"
    // with the same mask strip every other rating in the drawer uses.
    // Geometry is identical (same viewBox, rotational centre at cell
    // centre); size is set in CSS via --sh (.decoy-pc-stars).
    var infos = root.querySelectorAll('.kl_reviews__review__product_card__details__review_info');
    for (var ri = 0; ri < infos.length; ri++) {
      var info = infos[ri];
      if (info.dataset.decoyPcStarred) {
        if (!info.querySelector('.decoy-pc-stars')) {
          // Klaviyo re-rendered the node wholesale and wiped our strip —
          // fall through and re-decorate from scratch.
          delete info.dataset.decoyPcStarred;
        } else {
          // Klaviyo's React can re-insert its original "★ · N reviews"
          // nodes BESIDE our strip (foreign siblings don't exist in its
          // vdom, so they survive reconciliation) — the card then reads
          // "2 REVIEWS ★ · 2 reviews". Prune every child that isn't ours,
          // including bare text nodes (the CSS guard hides foreign
          // ELEMENTS between ticks, but text nodes need removal).
          var fk = info.childNodes;
          for (var fkI = fk.length - 1; fkI >= 0; fkI--) {
            var fn = fk[fkI];
            if (fn.nodeType === 1 &&
                (fn.classList.contains('decoy-klav-stars') || fn.classList.contains('decoy-pc-count'))) continue;
            info.removeChild(fn);
          }
          continue;
        }
      }
      var m3 = (info.textContent || '').match(/([\d.]+)\s*★\s*·\s*([\d,]+)\s+reviews?/i);
      if (!m3) continue;
      var pcRating = parseFloat(m3[1]);
      if (!(pcRating >= 0 && pcRating <= 5)) continue;
      var pcPct = (pcRating / 5) * 100;
      info.innerHTML =
        '<span class="decoy-klav-stars decoy-pc-stars" aria-hidden="true">' +
          '<span class="decoy-klav-stars-empty"></span>' +
          '<span class="decoy-klav-stars-fill" style="width:' + pcPct + '%"></span>' +
        '</span>' +
        '<span class="decoy-pc-count">' + m3[2] + ' reviews</span>';
      info.dataset.decoyPcStarred = '1';
    }
  }

  function watchKlaviyoWidget() {
    var root = document.getElementById('klaviyo-reviews-all');
    if (!root) return;
    decorateKlaviyoWidget();
    new MutationObserver(function () {
      // Re-tag: if Klaviyo re-renders (pagination, filter change), the
      // `data-decoy-cards-tagged` flag still points at the old DOM, so
      // clear it when the previously-tagged cards have been detached.
      if (root.dataset.decoyCardsTagged &&
          !root.querySelector('.decoy-review-card')) {
        delete root.dataset.decoyCardsTagged;
      }
      decorateKlaviyoWidget();
    }).observe(root, { childList: true, subtree: true });
  }

  function init() {
    probeKlaviyo();       // detect ad/tracker blocking up front
    bindDrawer();
    watchProductClose();  // close the drawer when navigating back to the landing page
    watchInfoPanel();
    watchKlaviyoWidget();
    watchCartOpen();
    ensureBadge();        // in case a product is already open
    startLiveAggregate(); // upgrade to live values
    watchLightboxForZoom(); // enable pinch-zoom on review photo lightbox
    interceptReviewPhotoClicks(); // bypass Klaviyo's broken per-review gallery
  }

  /* ---- DECOY lightbox for in-card review photos ----
     Klaviyo's native lightbox builds its gallery from the media-filtered
     review index (client_reviews/...&media=true), which lags behind the main
     feed — a new review's photos can be missing from it for days. When that
     happens, clicking the review's photo opens the WRONG review's gallery
     (the widget falls back to the first indexed media review). Seen live
     2026-06-09: jane's two photos (review 19272957) opened Luke's five-photo
     gallery because media=true returned only Luke's review (18407016).
     Nothing theme-side can heal Klaviyo's index, so we don't use it: clicks
     on photos inside a review card are intercepted (capture phase, before
     Klaviyo's React handler) and shown in our own lightbox, built from the
     card's own <img> srcs — which are always correct. Klaviyo's lightbox
     still serves the featured-photos strip in the summary block, where the
     gallery and the index are the same thing and can't disagree. */
  var LB_ID = 'DECOY_REVIEW_LIGHTBOX';

  function fullSizeSrc(src) {
    // Thumbnails are served via Klaviyo's resizer at 260-300px; request a
    // bigger render for the lightbox. The featured-strip variants carry
    // resizing_type:fill (a center-cropped square) — drop it so the full
    // render keeps the photo's real aspect. Non-resizer URLs pass through.
    return src.replace(/\/width:\d+\/height:\d+(?:\/resizing_type:\w+)?\//,
                       '/width:1200/height:1200/');
  }

  function closeDecoyLightbox() {
    var lb = document.getElementById(LB_ID);
    if (!lb) return;
    if (lb.__decoyCleanup) lb.__decoyCleanup();
    lb.parentNode.removeChild(lb);
  }

  /* Anchor the photo column over the reviews panel: the column IS the
     panel's box, and the photo is contained inside it with generous
     padding on all sides (see .drlb-col's CSS padding). Keeping the box
     fixed means the photo doesn't wander with each image's aspect ratio.
     On the full-screen drawer (≤1200px) the panel spans the viewport, so
     the column does too. */
  function anchorLightboxColumn(lb) {
    var col = lb.querySelector('.drlb-col');
    var panel = document.getElementById('DECOY_REVIEWS_PANEL');
    var pr = panel && panel.getBoundingClientRect();
    if (!pr || !pr.width) {
      col.style.left = '0';
      col.style.width = '100%';
      return;
    }
    col.style.left = Math.round(pr.left) + 'px';
    col.style.width = Math.round(pr.width) + 'px';
    // 12% of the PANEL width per side (CSS % padding would resolve against
    // the viewport-wide lightbox container instead).
    var pad = Math.round(pr.width * 0.12);
    col.style.paddingLeft = pad + 'px';
    col.style.paddingRight = pad + 'px';
  }

  function openDecoyLightbox(srcs, startIdx) {
    closeDecoyLightbox();
    var idx = Math.max(0, Math.min(srcs.length - 1, startIdx || 0));

    var lb = document.createElement('div');
    lb.id = LB_ID;
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-label', 'Review photo');
    lb.innerHTML =
      '<div class="drlb-col" data-pinch-zoom><img class="drlb-img" alt="Review photo"></div>' +
      '<button type="button" class="drlb-close" aria-label="Close photo"><svg class="decoy-icon ppc-desktop" aria-hidden="true" focusable="false"><use href="#i-close"></use></svg></button>' +
      (srcs.length > 1 ? '<button type="button" class="drlb-nav drlb-prev" aria-label="Previous photo">&#x2039;</button>' +
                         '<button type="button" class="drlb-nav drlb-next" aria-label="Next photo">&#x203A;</button>' : '');
    document.body.appendChild(lb);

    var col = lb.querySelector('.drlb-col');
    var img = lb.querySelector('.drlb-img');
    var closeBtn = lb.querySelector('.drlb-close');

    function positionChrome() {
      // X pinned to the photo's top-right corner, like the Klaviyo reskin.
      // Skip while pinch-zoomed (inline transform warps the measured rect).
      if (col.style.transform) return;
      var r = img.getBoundingClientRect();
      if (!r.width) return;
      closeBtn.style.top = Math.max(0, r.top) + 'px';
      closeBtn.style.left = (r.right - closeBtn.offsetWidth) + 'px';
      var prev = lb.querySelector('.drlb-prev');
      var next = lb.querySelector('.drlb-next');
      if (prev && next) {
        var mid = r.top + r.height / 2 - prev.offsetHeight / 2;
        prev.style.top = mid + 'px'; prev.style.left = r.left + 'px';
        next.style.top = mid + 'px'; next.style.left = (r.right - next.offsetWidth) + 'px';
      }
    }
    function show(i) {
      idx = (i + srcs.length) % srcs.length;
      // Reset any pinch-zoom transform left on the column before swapping.
      col.style.transform = '';
      // Chrome (X + arrows) stays hidden until the focused image renders —
      // unpositioned fixed buttons would otherwise flash at a stray spot.
      lb.classList.add('drlb-loading');
      // Snappy open: show the 300px thumbnail instantly (it's already in
      // the browser cache from the card/strip), then swap in the full-size
      // render once it arrives. Same image, same aspect — no layout shift.
      var thumb = srcs[idx];
      var full = fullSizeSrc(thumb);
      img.style.width = ''; // drop the previous image's pinned size
      img.src = thumb;
      if (full !== thumb) {
        var pre = new Image();
        var forIdx = idx;
        pre.onload = function () { if (idx === forIdx) img.src = full; };
        pre.src = full;
      }
      if (img.complete) onImgRendered();
    }
    function sizeImg() {
      // Pin the img to its final contained size. max-width/height alone
      // won't upscale a small thumbnail, so the photo would render small
      // and then pop larger when the full-size file lands; an explicit
      // width keeps the displayed size constant across the swap (the
      // thumb shows briefly soft instead).
      var cs = getComputedStyle(col);
      var bw = col.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      var bh = col.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      if (!img.naturalWidth || bw <= 0 || bh <= 0) return;
      var s = Math.min(bw / img.naturalWidth, bh / img.naturalHeight);
      img.style.width = Math.round(img.naturalWidth * s) + 'px';
      img.style.height = 'auto';
    }
    function onImgRendered() {
      sizeImg();
      positionChrome();
      lb.classList.remove('drlb-loading');
    }
    img.addEventListener('load', onImgRendered);
    // Warm the cache for the rest of the gallery so prev/next feel instant.
    setTimeout(function () {
      for (var p = 0; p < srcs.length; p++) {
        if (p !== startIdx) (new Image()).src = fullSizeSrc(srcs[p]);
      }
    }, 300);

    lb.addEventListener('click', function (e) {
      if (e.target.closest('.drlb-nav')) {
        show(idx + (e.target.closest('.drlb-next') ? 1 : -1));
        return;
      }
      if (e.target.closest('img')) return; // photo itself: leave for zoom
      closeDecoyLightbox();                // X or anywhere outside the photo
    });

    // Single-finger horizontal swipe = navigate (only while not zoomed;
    // image-zoom.js owns multi-touch and zoomed-state panning).
    var touchX = null;
    function onTS(e) { touchX = (e.touches.length === 1 && !col.style.transform) ? e.touches[0].clientX : null; }
    function onTE(e) {
      if (touchX === null || col.style.transform) return;
      var dx = e.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(dx) > 50 && srcs.length > 1) show(idx + (dx < 0 ? 1 : -1));
    }
    function onKey(e) {
      if (e.key === 'Escape') { closeDecoyLightbox(); e.stopPropagation(); }
      else if (e.key === 'ArrowRight') show(idx + 1);
      else if (e.key === 'ArrowLeft') show(idx - 1);
    }
    lb.addEventListener('touchstart', onTS, { passive: true });
    lb.addEventListener('touchend', onTE, { passive: true });
    document.addEventListener('keydown', onKey, true);
    function onResize() { anchorLightboxColumn(lb); sizeImg(); positionChrome(); }
    window.addEventListener('resize', onResize);
    lb.__decoyCleanup = function () {
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
    };

    anchorLightboxColumn(lb);
    show(idx);
  }

  function interceptReviewPhotoClicks() {
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var root = document.getElementById('klaviyo-reviews-all');
      if (!root) return;

      // In-card review photos: gallery = that card's own photos.
      var img = e.target.closest('img.kl_reviews__review__image');
      var scope = null, sel = '';
      if (img) {
        scope = img.closest('.kl_reviews__review_item, .decoy-review-card');
        sel = 'img.kl_reviews__review__image';
      } else {
        // Featured-photos strip in the summary block. Klaviyo builds the
        // strip from the main review feed but its lightbox gallery from the
        // lagging media index, so they disagree the same way the cards do
        // — intercept these too. Gallery = the strip's own photos.
        img = e.target.closest('img.kl_reviews__summary__image_photo');
        if (img) {
          scope = img.closest('[class*="kl_reviews__summary__image_container"]') || root;
          sel = 'img.kl_reviews__summary__image_photo';
        }
      }
      if (!img || !scope || !root.contains(img)) return;

      e.preventDefault();
      e.stopPropagation(); // beat Klaviyo's React handler (we're in capture)
      var imgs = scope.querySelectorAll(sel);
      var srcs = [], start = 0;
      for (var i = 0; i < imgs.length; i++) {
        if (imgs[i] === img) start = i;
        srcs.push(imgs[i].currentSrc || imgs[i].src);
      }
      openDecoyLightbox(srcs, start);
    }, true);
  }

  /* Enhance the review-photo lightbox (Klaviyo portals it to <body>):
       1. Tag the photo column .kl_reviews__lightbox__left with data-pinch-zoom
          so image-zoom.js (same pinch/pan as product images) scales it. Tagging
          the COLUMN (not the img) means its z-index:1000 lift during zoom rises
          above the close X automatically, then resets on release.
       2. Anchor the close X to the top-right corner of the active photo. */
  function watchLightboxForZoom() {
    function setup(lb) {
      var left = lb.querySelector('.kl_reviews__lightbox__left');
      if (left && !left.hasAttribute('data-pinch-zoom')) left.setAttribute('data-pinch-zoom', '');
      // Tap anywhere outside the photo + the X to close.
      if (!lb.__decoyCloseBound) {
        lb.__decoyCloseBound = true;
        lb.addEventListener('click', function (e) {
          if (e.target.closest('img') || e.target.closest('.kl_reviews__close_button')) return;
          var cb = lb.querySelector('.kl_reviews__close_button');
          if (cb) cb.click();
        });
      }
      centerInPanel(lb);
      positionClose(lb);
    }
    /* Desktop only: the lightbox renders the photo at its natural size pinned to
       the LEFT of the screen, but the reviews drawer is a 33vw panel on the
       right — so the photo lands far from the panel you clicked from. Shift the
       whole content wrapper horizontally so the photo's centre lines up with the
       panel's centre. Measured (not swiper.update(), which collapses the slide)
       so it never changes the photo's size. ≤1200px the drawer is full-screen
       and the photo already centres — leave it exactly as the original. */
    function centerInPanel(lb) {
      var cw = lb.querySelector('.kl_reviews__lightbox__content_wrapper');
      if (!cw) return;
      if (window.innerWidth <= 1200) { if (cw.style.transform) cw.style.transform = ''; return; }
      var left = lb.querySelector('.kl_reviews__lightbox__left');
      if (left && left.style.transform) return; // mid pinch-zoom — don't fight it
      var slide = lb.querySelector('.swiper-slide-active') || lb.querySelector('.swiper-slide');
      var img = slide && slide.querySelector('img');
      if (!img) return;
      cw.style.transform = '';                  // measure the photo unshifted
      var r = img.getBoundingClientRect();
      if (!r.width) return;
      var current = (r.left + r.right) / 2;
      var panelW = window.innerWidth * 0.33;     // mirrors the 33vw drawer
      var desired = window.innerWidth - panelW / 2;
      cw.style.transform = 'translateX(' + Math.round(desired - current) + 'px)';
    }
    function positionClose(lb) {
      var btn = lb.querySelector('.kl_reviews__close_button');
      var left = lb.querySelector('.kl_reviews__lightbox__left');
      // The X is position:fixed. centerInPanel() puts a transform on the content
      // wrapper, which would make that wrapper the containing block for the fixed
      // X (shoving it off-screen by the shift amount). Re-parent the X onto the
      // un-transformed backdrop (lb) so its fixed coords stay viewport-relative.
      if (btn && btn.parentElement !== lb) lb.appendChild(btn);
      if (!btn) return;
      // Keep the X hidden until it's actually anchored to the photo — otherwise
      // it flashes at its default (top-left) corner for a frame before the photo
      // has rendered and we can measure it.
      btn.style.position = 'fixed';
      // Don't move the X while the photo is zoomed/panned — image-zoom sets an
      // inline transform on the column, which warps the img's measured rect.
      // The zoomed photo covers the X anyway; reposition only once settled.
      if (left && left.style.transform) return;
      var slide = lb.querySelector('.swiper-slide-active') || lb.querySelector('.swiper-slide');
      var img = slide && slide.querySelector('img');
      var r = img && img.getBoundingClientRect();
      if (!r || !r.width) { btn.style.visibility = 'hidden'; return; }
      btn.style.top = Math.max(0, r.top) + 'px';
      btn.style.left = (r.right - btn.offsetWidth) + 'px';
      btn.style.right = 'auto';
      btn.style.bottom = 'auto';
      btn.style.visibility = 'visible';
    }
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        for (var j = 0; j < muts[i].addedNodes.length; j++) {
          var n = muts[i].addedNodes[j];
          if (n.nodeType !== 1) continue;
          var lb = n.classList && n.classList.contains('kl_reviews__lightbox_background')
            ? n : (n.querySelector && n.querySelector('.kl_reviews__lightbox_background'));
          if (!lb) continue;
          (function (lb) {
            // Run once immediately so the X is hidden from the first frame (not
            // left flashing at top-left until the first interval tick), then
            // re-run while the imgs load + swiper settles, on every slide change
            // (active-class flips), and on resize.
            setup(lb);
            var tries = 0, iv = setInterval(function () { setup(lb); if (++tries > 25) clearInterval(iv); }, 100);
            // Observer re-runs setup() (re-centres + re-positions the X on slide
            // changes). setup() only sets inline styles, never classes, so it
            // can't re-trigger this class observer into a loop.
            new MutationObserver(function () { setup(lb); })
              .observe(lb, { attributes: true, attributeFilter: ['class'], subtree: true, childList: true });
            // Re-centre + reposition the X on resize (e.g. crossing the 1201px
            // breakpoint, where the shift turns on/off).
            var onR = function () { setup(lb); };
            window.addEventListener('resize', onR);
            window.addEventListener('orientationchange', onR);
            new MutationObserver(function (m2, self) {
              if (!document.body.contains(lb)) {
                window.removeEventListener('resize', onR);
                window.removeEventListener('orientationchange', onR);
                clearInterval(iv);
                self.disconnect();
              }
            }).observe(document.body, { childList: true });
          })(lb);
        }
      }
    }).observe(document.body, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
